import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWallet } from '../contexts/WalletContext'
import {
  getMyAmbTransactions,
  type AmbHistoryRoundEvent,
  type AmbHistoryRoundSummary,
} from '../services/ambHistoryService'

const PAGE_SIZE = 20

export type FinancialLotteryReportMode = 'all' | 'settled' | 'pending'
type DatePreset = 'all' | 'today' | '7d' | '30d'

interface AppliedFilters {
  statusFilter: string
  startDate: string
  endDate: string
}

const ALL_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'OPEN', label: 'กำลังเล่น' },
  { value: 'WAITING', label: 'รอผล' },
  { value: 'SETTLED', label: 'จบแล้ว' },
  { value: 'COMPLETED', label: 'สำเร็จ' },
  { value: 'REFUND', label: 'คืนเงิน' },
  { value: 'VOID', label: 'โมฆะ' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
]

const SETTLED_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'SETTLED', label: 'จบแล้ว' },
  { value: 'COMPLETED', label: 'สำเร็จ' },
  { value: 'REFUND', label: 'คืนเงิน' },
  { value: 'VOID', label: 'โมฆะ' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
]

const PENDING_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'OPEN', label: 'กำลังเล่น' },
  { value: 'WAITING', label: 'รอผล' },
]

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'กำลังเล่น',
  WAITING: 'รอผล',
  SETTLED: 'จบแล้ว',
  COMPLETED: 'สำเร็จ',
  REFUND: 'คืนเงิน',
  VOID: 'โมฆะ',
  CANCELLED: 'ยกเลิก',
}

const STATUS_CLASS: Record<string, string> = {
  OPEN: 'bgc-13',
  WAITING: 'bgc-13',
  SETTLED: 'bgc-11',
  COMPLETED: 'bgc-11',
  REFUND: 'bgc-6',
  VOID: 'bgc-16',
  CANCELLED: 'bgc-16',
}

const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  PLACE_BET: 'วางเดิมพัน',
  SETTLE_BET: 'สรุปผลเดิมพัน',
  CANCEL_BET: 'ยกเลิกเดิมพัน',
  ADJUST_BET: 'ปรับยอดเดิมพัน',
  ROLLBACK: 'ย้อนรายการ',
  WIN_REWARD: 'รับรางวัล',
  PLACE_TIP: 'เดิมพันพิเศษ',
  CANCEL_TIP: 'ยกเลิกเดิมพันพิเศษ',
  VOID_SETTLED: 'โมฆะผลเดิมพัน',
  ADJUST_BALANCE: 'ปรับยอด',
  CHECK_BALANCE: 'ตรวจสอบยอด',
  SETTLE_SINGLE_STATE: 'สรุปผลเกม',
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatBangkokDate(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value || '1970'
  const month = parts.find((part) => part.type === 'month')?.value || '01'
  const day = parts.find((part) => part.type === 'day')?.value || '01'
  return `${year}-${month}-${day}`
}

function shiftBangkokDate(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00+07:00`)
  date.setUTCDate(date.getUTCDate() + days)
  return formatBangkokDate(date)
}

function toBangkokApiDate(dateString: string, boundary: 'start' | 'end'): string {
  if (!dateString) return ''
  return boundary === 'start'
    ? `${dateString}T00:00:00.000+07:00`
    : `${dateString}T23:59:59.999+07:00`
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } {
  if (preset === 'all') {
    return { startDate: '', endDate: '' }
  }

  const todayBangkok = formatBangkokDate(new Date())
  if (preset === 'today') {
    return { startDate: todayBangkok, endDate: todayBangkok }
  }

  return {
    startDate: shiftBangkokDate(todayBangkok, preset === '7d' ? -6 : -29),
    endDate: todayBangkok,
  }
}

function getStatusLabel(status?: string): string {
  const key = String(status || '').toUpperCase()
  return STATUS_LABEL[key] || (status || '-')
}

function getTransactionTypeLabel(type?: string): string {
  return TRANSACTION_TYPE_LABEL[String(type || '').toUpperCase()] || String(type || '-')
}

function getNetResult(summary: AmbHistoryRoundSummary): number {
  return Number(summary.netAmount) || 0
}

function getAmountColorClass(value?: number | null): string {
  const amount = Number(value) || 0
  if (amount > 0) return 'c-green'
  if (amount < 0) return 'c-red'
  return 'c-dark'
}

function getAmountAccentStyle(value?: number | null): React.CSSProperties | undefined {
  const amount = Number(value) || 0
  if (amount < 0) {
    return { color: '#dc2626' }
  }
  if (amount > 0) {
    return { color: '#16a34a' }
  }
  return undefined
}

function getCardTitle(mode: FinancialLotteryReportMode, summary: AmbHistoryRoundSummary): string {
  const statusKey = String(summary.status || '').toUpperCase()
  if (mode === 'pending') return 'รอผลการเล่นพนัน'
  if (statusKey === 'REFUND') return 'สรุปผลคืนเงิน'
  if (statusKey === 'VOID' || statusKey === 'CANCELLED') return 'สรุปผลยกเลิก'
  return 'สรุปรอบการเล่น'
}

function getCardAmountClass(mode: FinancialLotteryReportMode, summary: AmbHistoryRoundSummary): string {
  if (mode === 'pending') return 'c-red'
  const net = getNetResult(summary)
  if (net > 0) return 'c-green'
  if (net < 0) return 'c-red'
  return 'c-dark'
}

function getPrimaryAmountText(
  mode: FinancialLotteryReportMode,
  summary: AmbHistoryRoundSummary,
  formatCurrency: (value: number) => string,
): string {
  if (mode === 'pending') {
    return `-${formatCurrency(Number(summary.amount) || 0)}`
  }

  const net = getNetResult(summary)
  if (net > 0) return `+${formatCurrency(net)}`
  if (net < 0) return `-${formatCurrency(Math.abs(net))}`
  return formatCurrency(0)
}

function getSecondaryAmountText(
  summary: AmbHistoryRoundSummary,
  formatCurrency: (value: number) => string,
): string {
  return formatCurrency(Number(summary.balanceAfter) || 0)
}

function getSecondaryLabel(mode: FinancialLotteryReportMode): string {
  return mode === 'pending' ? 'ยอดเงินหลังล่าสุด' : 'ยอดเงินหลังจบรอบ'
}

function summarizeEvents(events: AmbHistoryRoundEvent[] = [], formatCurrency: (value: number) => string): string {
  if (!Array.isArray(events) || events.length === 0) return '-'
  const summary = events
    .slice(0, 4)
    .map((event) => {
      const typeLabel = getTransactionTypeLabel(event.transactionType)
      const bet = Number(event.amount) || 0
      const win = Number(event.winAmount) || 0
      const net = Number(event.netAmount) || 0
      return `${typeLabel} เดิมพัน ${formatCurrency(bet)} ชนะ ${formatCurrency(win)} สุทธิ ${net > 0 ? '+' : ''}${formatCurrency(net)}`
    })
    .join(', ')
  return events.length > 4 ? `${summary} และอีก ${events.length - 4} รายการ` : summary
}

function getRoundHint(summary: AmbHistoryRoundSummary): string {
  if ((summary.zeroBetWinEventCount || 0) > 0 && (summary.amount || 0) > 0) {
    return `รอบนี้มีโบนัส/ผลย่อย ${summary.zeroBetWinEventCount} รายการ โดยยอดเดิมพันหลักถูกหักไปแล้ว`
  }
  if ((summary.eventCount || 0) > 1) {
    return `รอบนี้มี ${summary.eventCount} เหตุการณ์ จึงถูกรวมเป็น 1 รายการ`
  }
  return '1 รายการนี้คือสรุปของ 1 รอบการเล่น'
}

function buildSearchText(summary: AmbHistoryRoundSummary): string {
  return [
    summary.transactionId,
    summary.roundId,
    summary.betId,
    summary.gameName,
    summary.gameCode,
    summary.productId,
    summary.status,
    getStatusLabel(summary.status),
    formatDateTime(summary.createdAt || summary.updatedAt),
    ...(summary.transactionIds || []),
    ...(summary.events || []).map((event) => `${event.transactionType} ${event.status}`),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

interface FinancialLotteryReportListProps {
  mode: FinancialLotteryReportMode
}

const FinancialLotteryReportList: React.FC<FinancialLotteryReportListProps> = ({ mode }) => {
  const { formatCurrency } = useWallet()
  const [roundSummaries, setRoundSummaries] = useState<AmbHistoryRoundSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [datePreset, setDatePreset] = useState<DatePreset>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    statusFilter: 'all',
    startDate: '',
    endDate: '',
  })
  const abortControllerRef = useRef<AbortController | null>(null)

  const statusOptions = mode === 'pending'
    ? PENDING_STATUS_OPTIONS
    : mode === 'settled'
      ? SETTLED_STATUS_OPTIONS
      : ALL_STATUS_OPTIONS
  const emptyText = mode === 'pending'
    ? 'ยังไม่มีรายการ AMB ที่รอผล'
    : mode === 'settled'
      ? 'ยังไม่มีประวัติการเดิมพัน AMB'
      : 'ยังไม่มีรายการรายงานการเงิน AMB'

  const loadSummaries = useCallback(async (pageNum: number = 1) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      setLoading(true)
      setError(null)
      const result = await getMyAmbTransactions({
        mode,
        page: pageNum,
        limit: PAGE_SIZE,
        ...(appliedFilters.statusFilter !== 'all' ? { status: appliedFilters.statusFilter } : {}),
        ...(appliedFilters.startDate ? { startDate: toBangkokApiDate(appliedFilters.startDate, 'start') } : {}),
        ...(appliedFilters.endDate ? { endDate: toBangkokApiDate(appliedFilters.endDate, 'end') } : {}),
        signal: controller.signal,
      })
      setRoundSummaries(result.roundSummaries || [])
      setTotal(result.pagination?.total || 0)
      setPage(result.pagination?.page || pageNum)
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        return
      }
      setError(e?.message || 'โหลดข้อมูลไม่สำเร็จ')
      setRoundSummaries([])
      setTotal(0)
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false)
      }
    }
  }, [appliedFilters, mode])

  useEffect(() => {
    loadSummaries(1)
  }, [loadSummaries])

  useEffect(() => () => {
    abortControllerRef.current?.abort()
  }, [])

  const filteredSummaries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return roundSummaries
    return roundSummaries.filter((summary) => buildSearchText(summary).includes(q))
  }, [roundSummaries, searchQuery])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset)
    const range = getPresetRange(preset)
    setStartDate(range.startDate)
    setEndDate(range.endDate)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    handlePresetChange('all')
    setAppliedFilters({
      statusFilter: 'all',
      startDate: '',
      endDate: '',
    })
  }

  const handleApplyFilters = () => {
    setPage(1)
    setAppliedFilters({
      statusFilter,
      startDate,
      endDate,
    })
  }

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    loadSummaries(nextPage)
  }

  return (
    <div className="row gap-2 mx--1">
      <div className="col-12 px-1">
        <div className="bdr-5 bgc-4 p-2">
          <div className="row gap-2 mx--1">
            <div className="col-12 px-1">
              <div className="input-group">
                <span className="input-group-text bgc-2 c-white border-0">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="text"
                  className="form-control bgc-2 c-white border-0"
                  placeholder="ค้นหาเกม, ผู้ให้บริการ, สถานะ, รหัสรายการ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery.trim() && (
                  <button type="button" className="btn bgc-2 c-white border-0" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-12 px-1">
              <div className="wrap gap-1">
                {(['all', 'today', '7d', '30d'] as DatePreset[]).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`px-2 py-1 bdr-5 border-0 ${datePreset === preset ? 'bgc-11 c-white' : 'bgc-2 c-white'}`}
                    disabled={loading}
                    onClick={() => handlePresetChange(preset)}
                  >
                    {preset === 'all' ? 'ทั้งหมด' : preset === 'today' ? 'วันนี้' : preset === '7d' ? '7 วัน' : '30 วัน'}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-6 px-1">
              <small className="c-dark d-block mb-1">จากวันที่</small>
              <input
                type="date"
                className="form-control bgc-2 c-white border-0"
                value={startDate}
                disabled={loading}
                onChange={(e) => {
                  setDatePreset('all')
                  setStartDate(e.target.value)
                }}
              />
            </div>

            <div className="col-6 px-1">
              <small className="c-dark d-block mb-1">ถึงวันที่</small>
              <input
                type="date"
                className="form-control bgc-2 c-white border-0"
                value={endDate}
                disabled={loading}
                onChange={(e) => {
                  setDatePreset('all')
                  setEndDate(e.target.value)
                }}
              />
            </div>

            <div className="col-12 px-1">
              <div className="wrap gap-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`px-2 py-1 bdr-5 border-0 ${statusFilter === option.value ? 'bgc-6 c-white' : 'bgc-2 c-white'}`}
                    disabled={loading}
                    onClick={() => setStatusFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-12 px-1">
              <div className="wrap gap-1">
                <button
                  type="button"
                  className="bgc-11 c-white px-2 py-1 bdr-5 border-0"
                  disabled={loading}
                  onClick={handleApplyFilters}
                >
                  <i className={`me-1 fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'}`} /> {loading ? 'กำลังโหลด...' : 'ใช้ตัวกรอง'}
                </button>
                <button type="button" className="bgc-16 c-white px-2 py-1 bdr-5 border-0" disabled={loading} onClick={handleClearFilters}>
                  ล้างตัวกรอง
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 px-1">
        <div className="bdr-5 bdc-2 bgc-4 p-2">
          <div className="wrap justify-content-between">
            <small className="c-dark">
              {loading ? 'กำลังดึงข้อมูลใหม่ กรุณารอสักครู่...' : 'ค้นหาในรายการที่โหลดอยู่ของหน้าปัจจุบัน'}
            </small>
            <small className="c-dark">
              แสดง {filteredSummaries.length} จาก {roundSummaries.length} รายการ | ทั้งหมด {total} รายการ
            </small>
          </div>
        </div>
      </div>

      {loading && roundSummaries.length === 0 && (
        <div className="col-12 px-1">
          <div className="bdr-5 bgc-4 p-3 text-center">
            <i className="fas fa-spinner fa-spin fs-24 c-white" />
            <p className="c-white mt-2 mb-0">กำลังโหลดรายงาน AMB...</p>
          </div>
        </div>
      )}

      {!loading && error && roundSummaries.length === 0 && (
        <div className="col-12 px-1">
          <div className="bdr-5 bgc-4 p-3 text-center">
            <p className="c-red mb-2">{error}</p>
            <button type="button" className="bgc-11 c-white px-2 py-1 bdr-5 border-0" onClick={() => loadSummaries(1)}>
              โหลดใหม่
            </button>
          </div>
        </div>
      )}

      {!loading && !error && filteredSummaries.length === 0 && (
        <div className="col-12 px-1">
          <div className="bdr-5 bgc-4 p-3 text-center">
            <h5 className="c-red mb-0">{searchQuery.trim() ? 'ไม่พบรายการตามคำค้นหา' : emptyText}</h5>
          </div>
        </div>
      )}

      {!loading && !error && filteredSummaries.map((summary) => {
        const statusKey = String(summary.status || (mode === 'pending' ? 'OPEN' : 'SETTLED')).toUpperCase()
        const refId = summary.roundId || summary.transactionId || summary._id

        return (
          <div key={summary._id} className="col-12 px-1">
            <div className="bdr-5 bdc-2 bgc-4 p-2">
              <div className="wrap t-medium justify-content-between mb-2">
                <h6
                  className={`wrap justify-content-center bdr-5 ${STATUS_CLASS[statusKey] || 'bgc-13'} px-1 c-white text-center`}
                  style={{ minWidth: 140 }}
                >
                  {getStatusLabel(summary.status)}
                </h6>
                <h5 className="ms-auto text-end c-red">{getCardTitle(mode, summary)}</h5>
              </div>

              <div
                className="d-grid gap-2"
                style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
              >
                <h4 className={getCardAmountClass(mode, summary)} style={getAmountAccentStyle(mode === 'pending' ? -Math.abs(Number(summary.amount) || 0) : getNetResult(summary))}>
                  {getPrimaryAmountText(mode, summary, formatCurrency)}
                </h4>

                <h4 className="text-end t-medium">
                  {getSecondaryAmountText(summary, formatCurrency)}
                </h4>

                <h6 className="pe-2">
                  {summary.productId || '-'} {summary.gameName ? `(${summary.gameName})` : ''}
                </h6>

                <h6 className="text-end">
                  {formatDateTime(summary.settledAt || summary.updatedAt || summary.createdAt)}
                </h6>
              </div>

              <div className="mt-2">
                <small className="c-dark d-block">
                  <span className="fw-bold">Round ID:</span> {summary.roundId || '-'}
                </small>
                <small className="c-dark d-block">
                  <span className="fw-bold">Transaction หลัก:</span> {summary.transactionId || '-'}
                </small>
                <small className="c-dark d-block">
                  <span className="fw-bold">ยอดก่อน:</span> {formatCurrency(Number(summary.balanceBefore) || 0)} |{' '}
                  <span className="fw-bold">ยอดหลัง:</span> {formatCurrency(Number(summary.balanceAfter) || 0)}
                </small>
                <small className="c-dark d-block">
                  <span className="fw-bold">เดิมพัน:</span> {formatCurrency(Number(summary.amount) || 0)} |{' '}
                  <span className="fw-bold">รับ:</span> {formatCurrency(Number(summary.winAmount) || 0)} |{' '}
                  <span className="fw-bold">สุทธิ:</span>{' '}
                  <span className={getAmountColorClass(getNetResult(summary))} style={getAmountAccentStyle(getNetResult(summary))}>
                    {getNetResult(summary) > 0 ? '+' : ''}{formatCurrency(getNetResult(summary))}
                  </span>
                </small>
                <small className="c-dark d-block">{getRoundHint(summary)}</small>
                <small className={`d-block ${getAmountColorClass(getNetResult(summary))}`} style={getAmountAccentStyle(getNetResult(summary))}>
                  <span className="fw-bold">เหตุการณ์ย่อย:</span> {summarizeEvents(summary.events, formatCurrency)}
                </small>
              </div>

              <div className="wrap justify-content-between align-items-center mt-2">
                <small className="c-dark">
                  {getSecondaryLabel(mode)} {formatCurrency(Number(summary.balanceAfter) || 0)} | {summary.eventCount || 0} เหตุการณ์
                </small>
                <button
                  type="button"
                  className="bgc-13 c-white px-1 bdr-5 border-0"
                  onClick={() => navigator.clipboard?.writeText(refId).catch(() => undefined)}
                >
                  <i className="fas fa-copy me-1" /> คัดลอก
                </button>
              </div>
            </div>
          </div>
        )
      })}

      {loading && roundSummaries.length > 0 && (
        <div className="col-12 px-1">
          <div className="bdr-5 bdc-2 bgc-4 p-3 text-center">
            <i className="fas fa-spinner fa-spin fs-24 c-white" />
            <p className="c-white mt-2 mb-1">กำลังประมวลผลตัวกรองและดึงข้อมูลล่าสุด...</p>
            <small className="c-dark">ปุ่มต่างๆ จะถูกปิดชั่วคราวเพื่อกันการยิง request ซ้ำ</small>
          </div>
        </div>
      )}

      {!searchQuery.trim() && total > 0 && (
        <div className="col-12 px-1">
          <div className="wrap justify-content-between align-items-center mt-1">
            <small className="c-dark">
              รายการ {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} จาก {total}
            </small>
            <div className="wrap gap-1">
              <button
                type="button"
                className="bgc-4 bdc-2 c-white px-2 py-1 bdr-5"
                disabled={page <= 1 || loading}
                onClick={() => goToPage(page - 1)}
              >
                ก่อนหน้า
              </button>
              <span className="c-white px-1">
                หน้า {page} / {totalPages}
              </span>
              <button
                type="button"
                className="bgc-4 bdc-2 c-white px-2 py-1 bdr-5"
                disabled={page >= totalPages || loading}
                onClick={() => goToPage(page + 1)}
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FinancialLotteryReportList
